/**
 * One community: its groups, its people, and the admin controls.
 *
 * The screen behind "View all". Three things on it, in the order they
 * matter: the groups you can open or join, the members, and — for an admin
 * — the controls that change either.
 *
 * Joining a group here is the point of a community. Every group is listed
 * whether or not you are in it, because a group you cannot see is a group
 * you can never join, and being in the community is exactly what entitles
 * you to join its groups.
 */

import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  communities as communityService, memberLabel, previewText, userMessage
} from '@zivapp/shared'
import Avatar from '../components/ui/Avatar.jsx'
import './CommunityInfo.css'

function Megaphone () {
  return (
    <span className="ci__megaphone" aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none">
        <path d="M4 9.5h3.2L14 5.2v13.6L7.2 14.5H4a1.5 1.5 0 0 1-1.5-1.5v-2a1.5 1.5 0 0 1 1.5-1.5Z"
          stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
        <path d="M17.4 9a4.4 4.4 0 0 1 0 6" stroke="currentColor" strokeWidth="1.5"
          strokeLinecap="round" />
      </svg>
    </span>
  )
}

export default function CommunityInfo () {
  const { communityId } = useParams()
  const navigate = useNavigate()

  const [community, setCommunity] = useState(null)
  const [groups, setGroups] = useState([])
  const [members, setMembers] = useState([])
  const [isLoading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(null)
  const [newGroup, setNewGroup] = useState('')

  const refresh = useCallback(async () => {
    try {
      const [detail, people] = await Promise.all([
        communityService.get(communityId),
        communityService.members(communityId).catch(() => [])
      ])
      setCommunity(detail)
      setMembers(people)
      setError(null)
    } catch (cause) {
      setError(cause)
    } finally {
      setLoading(false)
    }
  }, [communityId])

  useEffect(() => { refresh() }, [refresh])
  useEffect(
    () => communityService.subscribeToGroups(communityId, setGroups),
    [communityId]
  )

  const join = async group => {
    setBusy(group.id)
    try {
      await communityService.joinGroup(communityId, group.id)
      refresh()
    } finally {
      setBusy(null)
    }
  }

  const createGroup = async event => {
    event.preventDefault()
    const name = newGroup.trim()
    if (!name || busy) return

    setBusy('new')
    try {
      await communityService.createGroup(communityId, name)
      setNewGroup('')
      refresh()
    } catch (cause) {
      setError(cause)
    } finally {
      setBusy(null)
    }
  }

  const leave = async () => {
    try {
      await communityService.leave(communityId)
      navigate('/communities')
    } catch (cause) {
      // The last admin is refused, with a message saying what to do
      // instead. Shown rather than swallowed — it is the only way they
      // find out the rule exists.
      setError(cause)
    }
  }

  const remove = async () => {
    await communityService.delete(communityId)
    navigate('/communities')
  }

  if (isLoading) return <div className="ci ci--message">Loading community…</div>

  if (!community) {
    return (
      <div className="ci ci--message">
        <p>This community is no longer available.</p>
        <Link className="ci__back-link" to="/communities">Back to Communities</Link>
      </div>
    )
  }

  const announcement = groups.find(g => g.isAnnouncement)
  const others = groups.filter(g => !g.isAnnouncement)

  return (
    <div className="ci">
      <header className="ci__bar">
        <button
          type="button"
          className="ci__icon-btn"
          onClick={() => navigate('/communities')}
          aria-label="Back"
        >
          <svg viewBox="0 0 24 24" fill="none">
            <path d="M15 5 8 12l7 7" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <h1 className="ci__bar-title">Community info</h1>
      </header>

      <div className="ci__scroll">
        <section className="ci__hero">
          <Avatar name={community.name} url={community.avatarUrl} size={96} group />
          <h2 className="ci__name">{community.name}</h2>
          <p className="ci__meta">{memberLabel(community.memberCount)}</p>
          {community.description && (
            <p className="ci__description">{community.description}</p>
          )}
        </section>

        <section className="ci__section">
          <h3 className="ci__heading">Groups</h3>

          {announcement && (
            <Link className="ci__row" to={`/chats/${announcement.id}`}>
              <Megaphone />
              <span className="ci__row-main">
                <span className="ci__row-name">Announcements</span>
                <span className="ci__row-sub">
                  {community.isAdmin
                    ? 'You can post here'
                    : 'Only admins can post here'}
                </span>
              </span>
            </Link>
          )}

          {others.map(group => (
            <div className="ci__row" key={group.id}>
              {group.isJoined ? (
                <Link className="ci__row-hit" to={`/chats/${group.id}`}>
                  <Avatar name={group.name} url={group.avatarUrl} size={44} group />
                  <span className="ci__row-main">
                    <span className="ci__row-name">{group.name}</span>
                    <span className="ci__row-sub">
                      {group.lastMessage ? previewText(group.lastMessage) : 'No messages yet'}
                    </span>
                  </span>
                </Link>
              ) : (
                <>
                  <span className="ci__row-hit ci__row-hit--flat">
                    <Avatar name={group.name} url={group.avatarUrl} size={44} group />
                    <span className="ci__row-main">
                      <span className="ci__row-name">{group.name}</span>
                      <span className="ci__row-sub">You are not in this group</span>
                    </span>
                  </span>
                  <button
                    type="button"
                    className="ci__join"
                    onClick={() => join(group)}
                    disabled={busy === group.id}
                  >
                    {busy === group.id ? 'Joining…' : 'Join'}
                  </button>
                </>
              )}
            </div>
          ))}

          {others.length === 0 && (
            <p className="ci__none">No other groups yet.</p>
          )}

          {community.isAdmin && (
            <form className="ci__add-group" onSubmit={createGroup}>
              <input
                className="ci__input"
                value={newGroup}
                onChange={e => setNewGroup(e.target.value)}
                placeholder="New group name"
                aria-label="New group name"
                maxLength={100}
                data-testid="new-community-group"
              />
              <button
                type="submit"
                className="ci__add-btn"
                disabled={!newGroup.trim() || busy === 'new'}
                data-testid="add-community-group"
              >
                {busy === 'new' ? 'Adding…' : 'Add group'}
              </button>
            </form>
          )}
        </section>

        <section className="ci__section">
          <h3 className="ci__heading">{memberLabel(members.length)}</h3>
          {members.map(person => (
            <div className="ci__row" key={person.userId}>
              <span className="ci__row-hit ci__row-hit--flat">
                <Avatar name={person.displayName} url={person.avatarUrl} size={44} />
                <span className="ci__row-main">
                  <span className="ci__row-name">{person.displayName}</span>
                  <span className="ci__row-sub">{person.about || ''}</span>
                </span>
              </span>
              {person.role === 'admin' && <span className="ci__role">Admin</span>}
            </div>
          ))}
        </section>

        {error && <p className="ci__error" role="alert">{userMessage(error)}</p>}

        <section className="ci__section ci__section--danger">
          <button type="button" className="ci__danger" onClick={leave}>
            Leave community
          </button>
          {community.isAdmin && (
            <button
              type="button"
              className="ci__danger"
              onClick={remove}
              data-testid="delete-community"
            >
              Delete community
            </button>
          )}
          {/* Said plainly, because it is the part people get wrong: the
              groups are not deleted with the community. */}
          {community.isAdmin && (
            <p className="ci__note">
              Deleting removes the community and its announcement group. The
              other groups stay, as ordinary groups.
            </p>
          )}
        </section>
      </div>
    </div>
  )
}
